import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Button,
    Box,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress
} from '@mui/material';
import axios from '../../utils/axios';

const ExamTaking = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [exam, setExam] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [timeElapsed, setTimeElapsed] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showSubmitDialog, setShowSubmitDialog] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [randomizedChoices, setRandomizedChoices] = useState({});

    // Shuffle array function using Fisher-Yates algorithm
    const shuffleArray = useCallback((array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }, []);
    
    useEffect(() => {
        const fetchExam = async () => {
            try {
                const response = await axios.get(`/api/exams/${id}`);
                setExam(response.data);
                
                // Initialize randomized choices for each question
                const randomizedMap = {};
                response.data.questions.forEach(question => {
                    // For each question, create a mapping of original indices to shuffled choices
                    const originalChoices = question.choices;
                    const shuffledChoices = shuffleArray(originalChoices);
                    
                    // Create a mapping to track the original indices
                    const choiceMap = {};
                    shuffledChoices.forEach((choice, i) => {
                        const originalIndex = originalChoices.indexOf(choice);
                        choiceMap[i] = originalIndex;
                    });
                    
                    randomizedMap[question._id] = {
                        choices: shuffledChoices,
                        mapping: choiceMap
                    };
                });
                
                setRandomizedChoices(randomizedMap);
                setLoading(false);
            } catch (err) {
                setError(err.response?.data?.message || 'Error fetching exam');
                setLoading(false);
            }
        };

        fetchExam();
    }, [id, shuffleArray]);

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeElapsed(prev => prev + 1);
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleAnswerChange = (questionId, answer, choiceIndex) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: {
                answer: answer,
                originalIndex: randomizedChoices[questionId].mapping[choiceIndex]
            }
        }));
    };

    const handleNext = () => {
        if (currentQuestionIndex < exam.questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    const handleSubmit = async () => {
        setShowSubmitDialog(true);
    };

    const confirmSubmit = async () => {
        setSubmitting(true);
        try {
            // Convert answers to the format expected by the backend
            const formattedAnswers = {};
            Object.keys(answers).forEach(questionId => {
                formattedAnswers[questionId] = answers[questionId].answer;
            });
            
            const response = await axios.post(`/api/exams/${id}/submit`, {
                answers: formattedAnswers,
                duration: timeElapsed
            });
            
            navigate(`/exams/${id}/result`, {
                state: { 
                    result: response.data,
                    timeElapsed: formatTime(timeElapsed)
                }
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Error submitting exam');
            setShowSubmitDialog(false);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container>
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    const currentQuestion = exam.questions[currentQuestionIndex];
    const currentRandomizedChoices = randomizedChoices[currentQuestion._id]?.choices || [];
    const progress = ((currentQuestionIndex + 1) / exam.questions.length) * 100;

    return (
        <Container sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom>
                        {exam.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Time Elapsed: {formatTime(timeElapsed)}
                    </Typography>
                </Box>

                <LinearProgress variant="determinate" value={progress} sx={{ mb: 3 }} />

                <Typography variant="h6" gutterBottom>
                    Question {currentQuestionIndex + 1} of {exam.questions.length}
                </Typography>

                <Typography variant="body1" paragraph>
                    {currentQuestion.question}
                </Typography>

                {currentQuestion.imageUrl && (
                    <Box
                        component="img"
                        src={currentQuestion.imageUrl}
                        alt={`Question ${currentQuestionIndex + 1}`}
                        sx={{
                            mb: 3,
                            maxWidth: '100%',
                            maxHeight: 420,
                            objectFit: 'contain',
                            borderRadius: 1,
                            display: 'block'
                        }}
                    />
                )}

                <Box sx={{ mt: 3 }}>
                    {currentRandomizedChoices.map((choice, index) => (
                        <Button
                            key={index}
                            variant={answers[currentQuestion._id]?.answer === choice ? 'contained' : 'outlined'}
                            onClick={() => handleAnswerChange(currentQuestion._id, choice, index)}
                            sx={{ display: 'block', mb: 2, textAlign: 'left', width: '100%' }}
                        >
                            {choice}
                        </Button>
                    ))}
                </Box>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                        variant="outlined"
                        onClick={handlePrevious}
                        disabled={currentQuestionIndex === 0}
                    >
                        Previous
                    </Button>
                    <Button
                        variant="contained"
                        onClick={currentQuestionIndex === exam.questions.length - 1 ? handleSubmit : handleNext}
                    >
                        {currentQuestionIndex === exam.questions.length - 1 ? 'Submit' : 'Next'}
                    </Button>
                </Box>
            </Paper>

            <Dialog
                open={showSubmitDialog}
                onClose={() => setShowSubmitDialog(false)}
            >
                <DialogTitle>Submit Exam</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to submit your exam? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowSubmitDialog(false)}>Cancel</Button>
                    <Button
                        onClick={confirmSubmit}
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                    >
                        {submitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ExamTaking; 